"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef } from "react";
import { Socket, io } from "socket.io-client";
import { createClient } from "@/utils/supabase/client";
import { nowadaysApiUrl } from "@/utils/url-utils";
import { NAMESPACE_PATH } from "../_logic/constants";
import { AdminMessage } from "../_logic/types";

interface ClosedPayload {
  threadTs: string;
  closedAt: string;
}

interface UseSupportChatSocketArgs {
  /** Set to false during the brief unauthenticated flash so we don't attempt a connection. */
  enabled: boolean;
  onMessage: (payload: AdminMessage) => void;
  onClosed: (payload: ClosedPayload) => void;
  /** Called when the auth listener sees SIGNED_OUT — provider clears local state. */
  onSignOut: () => void;
}

/**
 * Manages the Socket.IO connection to the live-chat namespace.
 *
 * Lifecycle:
 * - Connects on mount (when `enabled`) using a fresh Supabase access token.
 * - Re-handshakes on TOKEN_REFRESHED with the new token (Socket.IO doesn't pick up
 *   auth changes on a live connection).
 * - Tears down on SIGNED_OUT or USER_UPDATED, or on unmount.
 * - A `connectingRef` guards against React Strict Mode's double-mount in dev.
 *
 * @param args - Enable flag and event handlers.
 */
export const useSupportChatSocket = ({ enabled, onMessage, onClosed, onSignOut }: UseSupportChatSocketArgs) => {
  // Keep handlers in refs so the effect doesn't re-run on every render.
  const onMessageRef = useRef(onMessage);
  const onClosedRef = useRef(onClosed);
  const onSignOutRef = useRef(onSignOut);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onClosedRef.current = onClosed;
    onSignOutRef.current = onSignOut;
  }, [onMessage, onClosed, onSignOut]);

  const connectingRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (connectingRef.current) return; // Strict Mode double-mount guard.
    connectingRef.current = true;

    const supabase = createClient();
    let cancelled = false;
    let socket: Socket | null = null;

    const connect = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled || !session) return;

      socket = io(nowadaysApiUrl(NAMESPACE_PATH), {
        auth: { token: session.access_token },
        transports: ["websocket"],
      });
      socketRef.current = socket;

      socket.on("support-chat:message", (payload: AdminMessage) => onMessageRef.current(payload));
      socket.on("support-chat:closed", (payload: ClosedPayload) => onClosedRef.current(payload));
      socket.on("connect_error", (err) => {
        Sentry.captureException(err, { tags: { feature: "support-chat", phase: "connect_error" } });
      });
    };

    connect();

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        socket?.disconnect();
        socket = null;
        socketRef.current = null;
        onSignOutRef.current();
        return;
      }
      if ((event === "TOKEN_REFRESHED" || event === "USER_UPDATED") && session) {
        if (!socket) return;
        // Debounce: when Supabase emits TOKEN_REFRESHED + USER_UPDATED in
        // quick succession (or multi-tab refresh storms broadcast within
        // milliseconds), coalesce into a single reconnect. Add 0–1500ms of
        // random jitter so N tabs don't slam the backend simultaneously when
        // an aligned refresh wave fires.
        if (reconnectTimer) clearTimeout(reconnectTimer);
        const jitter = Math.random() * 1500;
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          if (!socket) return;
          socket.auth = { token: session.access_token };
          socket.disconnect();
          socket.connect();
        }, jitter);
      }
    });

    return () => {
      cancelled = true;
      connectingRef.current = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      subscription.unsubscribe();
      socket?.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);
};
